import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Home,
  Users,
  GraduationCap,
  BookOpen,
  FileText,
  Heart,
  ClipboardCheck,
  ClipboardEdit,
  CreditCard,
  BrainCircuit,
  Calendar,
  MessageSquare,
  Crown,
  Settings,
  School,
  History,
  Trophy,
  BarChart3,
  Shield,
  UtensilsCrossed,
  Banknote,
  Brain,
  PieChart,
  Bus,
  FolderOpen,
  Wallet,
  NotebookPen,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuth } from '@/hooks/use-auth'
import { Badge } from '@/components/ui/badge'

export interface NavigationItem {
  name: string
  href: string
  icon: any
  roles: string[]
  badge?: string
  isPremium?: boolean
  description?: string
  schoolOnly?: boolean
}

// Define role-based navigation items
const navigationItems: NavigationItem[] = [
  // Common items
  {
    name: 'dashboard',
    href: '/app/dashboard',
    icon: Home,
    roles: ['student', 'teacher', 'parent', 'admin', 'school_admin', 'guard', 'bus_driver', 'cafeteria_staff', 'emf_agent', 'accountant', 'secretary', 'surveillant_general'],
    description: 'nav.dashboardDesc'
  },

  // Student-specific
  {
    name: 'lessons',
    href: '/app/lessons',
    icon: BookOpen,
    roles: ['student', 'teacher'],
    description: 'nav.lessonsDesc'
  },
  {
    name: 'assessments',
    href: '/app/assessments',
    icon: FileText,
    roles: ['student', 'teacher'],
    description: 'nav.assessmentsDesc'
  },
  {
    name: 'exam-prep',
    href: '/app/exam-prep',
    icon: ClipboardCheck,
    roles: ['student', 'parent', 'teacher'],
    description: 'nav.examPrepDesc'
  },
  {
    name: 'lesson-planner',
    href: '/app/lesson-planner',
    icon: Brain,
    roles: ['teacher'],
    badge: 'AI',
    description: 'nav.lessonPlannerDesc'
  },
  {
    name: 'ai-tutor',
    href: '/app/ai-tutor',
    icon: BrainCircuit,
    roles: ['student', 'parent', 'teacher'],
    badge: 'AI',
    description: 'nav.aiTutorDesc'
  },
  {
    name: 'gamification',
    href: '/app/gamification',
    icon: Trophy,
    roles: ['student'],
    description: 'nav.gamificationDesc'
  },
  {
    name: 'analytics',
    href: '/app/analytics',
    icon: BarChart3,
    roles: ['student', 'teacher'],
    description: 'nav.analyticsDesc'
  },

  // Teacher-specific (school context)
  {
    name: 'grade-entry',
    href: '/app/grade-entry',
    icon: ClipboardEdit,
    roles: ['teacher', 'admin', 'school_admin'],
    description: 'nav.gradeEntryDesc',
    schoolOnly: true,
  },
  {
    name: 'grade-history',
    href: '/app/grade-history',
    icon: History,
    roles: ['teacher', 'admin', 'school_admin'],
    description: 'nav.gradeHistoryDesc',
    schoolOnly: true,
  },
  {
    name: 'attendance',
    href: '/app/teacher-attendance',
    icon: ClipboardCheck,
    roles: ['teacher'],
    description: 'nav.attendanceDesc',
    schoolOnly: true,
  },
  {
    name: 'homework',
    href: '/app/teacher-homework',
    icon: BookOpen,
    roles: ['teacher'],
    description: 'nav.homeworkDesc',
    schoolOnly: true,
  },
  {
    name: 'students',
    href: '/app/students',
    icon: Users,
    roles: ['teacher', 'admin', 'school_admin', 'secretary', 'surveillant_general'],
    description: 'nav.studentsDesc'
  },
  {
    name: 'teachers',
    href: '/app/teachers',
    icon: GraduationCap,
    roles: ['admin', 'school_admin'],
    description: 'nav.teachersDesc',
    schoolOnly: true,
  },
  {
    name: 'teaching-record',
    href: '/app/teaching-record',
    icon: NotebookPen,
    roles: ['teacher'],
    description: 'nav.teachingRecordDesc',
    schoolOnly: true,
  },
  {
    name: 'payslips',
    href: '/app/teacher-payslips',
    icon: CreditCard,
    roles: ['teacher'],
    description: 'nav.payslipsDesc',
    schoolOnly: true,
  },
  {
    name: 'teacher-calendar',
    href: '/app/teacher-calendar',
    icon: Calendar,
    roles: ['teacher'],
    description: 'nav.teacherCalendarDesc',
    schoolOnly: true,
  },
  {
    name: 'suivi',
    href: '/app/suivi',
    icon: Brain,
    roles: ['teacher', 'admin', 'school_admin'],
    description: 'nav.suiviDesc',
    schoolOnly: true,
  },

  // Parent-specific (school context)
  {
    name: 'parent-portal',
    href: '/app/parent',
    icon: Heart,
    roles: ['parent'],
    description: 'nav.parentPortalDesc'
  },
  {
    name: 'parent-homework',
    href: '/app/parent?tab=devoirs',
    icon: BookOpen,
    roles: ['parent'],
    description: 'nav.parentHomeworkDesc',
    schoolOnly: true,
  },
  {
    name: 'parent-attendance',
    href: '/app/parent?tab=presences',
    icon: ClipboardCheck,
    roles: ['parent'],
    description: 'nav.parentAttendanceDesc',
    schoolOnly: true,
  },
  {
    name: 'report-cards',
    href: '/app/report-cards',
    icon: FileText,
    roles: ['parent', 'student', 'teacher', 'admin', 'school_admin'],
    description: 'nav.reportCardsDesc',
    schoolOnly: true,
  },
  {
    name: 'parent-communication',
    href: '/app/parent?tab=communication',
    icon: MessageSquare,
    roles: ['parent'],
    description: 'nav.parentCommunicationDesc',
    schoolOnly: true,
  },
  {
    name: 'parent-suivi',
    href: '/app/parent?tab=suivi',
    icon: Brain,
    roles: ['parent'],
    description: 'nav.parentSuiviDesc',
    schoolOnly: true,
  },

  // Admin-specific
  {
    name: 'admin-analytics',
    href: '/app/school-management?tab=analytics',
    icon: BarChart3,
    roles: ['admin', 'school_admin'],
    description: 'Analytics & Reports',
    schoolOnly: true,
  },
  {
    name: 'finance',
    href: '/app/school-management?tab=fees',
    icon: Wallet,
    roles: ['admin', 'school_admin', 'accountant'],
    description: 'Finance',
    schoolOnly: true,
  },
  {
    name: 'payroll',
    href: '/app/school-management?tab=payroll',
    icon: CreditCard,
    roles: ['admin', 'school_admin', 'accountant'],
    description: 'Payroll',
    schoolOnly: true,
  },
  {
    name: 'school-setup',
    href: '/app/school-management?tab=setup',
    icon: School,
    roles: ['admin', 'school_admin', 'secretary'],
    description: 'School Setup',
    schoolOnly: true,
  },
  {
    name: 'user-management',
    href: '/app/user-management',
    icon: Users,
    roles: ['admin', 'school_admin'],
    description: 'nav.userManagementDesc'
  },
  {
    name: 'transport',
    href: '/app/school-management?tab=transport',
    icon: Bus,
    roles: ['admin', 'school_admin'],
    description: 'Transport',
    schoolOnly: true,
  },
  {
    name: 'resources-dms',
    href: '/app/school-management?tab=documents',
    icon: FolderOpen,
    roles: ['admin', 'school_admin', 'secretary'],
    description: 'Resources & DMS',
    schoolOnly: true,
  },
  // Guard & Bus Driver – attendance scanning
  {
    name: 'attendance-scanner',
    href: '/app/attendance',
    icon: Shield,
    roles: ['guard', 'bus_driver', 'surveillant_general', 'admin', 'school_admin'],
    description: 'nav.attendanceScannerDesc',
  },
  // Cafeteria – admin, parents, students, cafeteria staff
  {
    name: 'cafeteria',
    href: '/app/cafeteria',
    icon: UtensilsCrossed,
    roles: ['admin', 'school_admin', 'parent', 'cafeteria_staff'],
    description: 'nav.cafeteriaDesc',
    schoolOnly: true,
  },
  // Multi-role items
  {
    name: 'communication',
    href: '/app/communication',
    icon: MessageSquare,
    roles: ['student', 'teacher', 'parent', 'admin', 'school_admin', 'secretary', 'surveillant_general'],
    description: 'nav.communicationDesc'
  },
  {
    name: 'settings',
    href: '/app/settings',
    icon: Settings,
    roles: ['student', 'teacher', 'parent', 'admin', 'school_admin', 'guard', 'bus_driver', 'cafeteria_staff', 'emf_agent', 'accountant', 'secretary', 'surveillant_general'],
    description: 'nav.settingsDesc'
  },
  {
    name: 'pricing',
    href: '/app/pricing',
    icon: CreditCard,
    roles: ['student', 'teacher', 'parent', 'admin', 'school_admin'],
    description: 'nav.pricingDesc',
    badge: 'upgrade'
  },
  // EMF Portal – EMF agents only
  {
    name: 'accounting',
    href: '/app/accounting',
    icon: PieChart,
    roles: ['admin', 'school_admin', 'accountant'],
    description: 'nav.accountingDesc',
    schoolOnly: true,
  },
  {
    name: 'emfPortal',
    href: '/app/emf-portal',
    icon: Banknote,
    roles: ['emf_agent'],
    description: 'nav.emfPortalDesc'
  },
]

interface RoleBasedNavigationProps {
  className?: string
  onItemClick?: () => void
}

export function RoleBasedNavigation({ className, onItemClick }: RoleBasedNavigationProps) {
  const location = useLocation()
  const { t } = useTranslation()
  const { user } = useAuth()

  if (!user) return null

  // Get user roles
  const userRoles = Array.isArray(user.roles) ? user.roles : [user.primary_role || 'student']
  const hasSchool = !!user.school_id
  // An independent teacher's personal classroom has no payroll, council or
  // report-card process behind it; those entries would only dead-end.
  const isPersonalClassroom = user.school_type === 'personal'

   // Filter navigation items based on user roles and school context
  const filteredNavigation = navigationItems.filter(item => {
    const hasRole = item.roles.some(role => userRoles.includes(role))
    if (!hasRole) return false
    if (item.schoolOnly && !hasSchool) return false
    if (isPersonalClassroom && ['payslips', 'suivi', 'report-cards', 'teacher-calendar', 'teaching-record'].includes(item.name)) return false
    // Hide pricing for school-affiliated staff — the school pays for them
    // Only show pricing for: individual users (no school) OR school_admin (manages billing)
    if (item.name === 'pricing' && hasSchool && !userRoles.includes('school_admin') && !userRoles.includes('admin')) return false
    return true
  })

  const isAdminUser = userRoles.includes('admin') || userRoles.includes('school_admin')
  const names = (items: string[]) => filteredNavigation.filter(item => items.includes(item.name))

  // Group navigation items by category. School admins get the redesigned IA from the handoff.
  const groupedNavigation = isAdminUser
    ? {
        pinned: names(['dashboard', 'students', 'finance']),
        recents: names(['attendance-scanner', 'communication']),
        main: names(['admin-analytics', 'grade-entry', 'report-cards']),
        management: names(['students', 'teachers', 'finance', 'payroll', 'attendance-scanner', 'cafeteria', 'transport', 'resources-dms', 'user-management']),
        communication: names(['communication', 'school-setup', 'settings', 'pricing']),
      }
    : {
        pinned: [],
        recents: [],
        main: names(['dashboard', 'lesson-planner', 'lessons', 'assessments', 'exam-prep', 'ai-tutor', 'gamification', 'analytics', 'report-cards']),
        management: names(['grade-entry', 'grade-history', 'attendance', 'homework', 'teaching-record', 'students', 'teachers', 'payslips', 'suivi', 'teacher-calendar', 'finance', 'payroll', 'user-management', 'attendance-scanner', 'cafeteria', 'transport', 'resources-dms', 'accounting', 'emfPortal']),
        communication: names(['parent-portal', 'parent-homework', 'parent-attendance', 'parent-communication', 'communication', 'settings', 'pricing']),
      }

  // A parent's canteen and transport are school life, not teaching tools
  const managementTitle = isAdminUser
    ? t('sidebar.management', 'Management')
    : userRoles.includes('teacher')
      ? t('sidebar.teachingTools', 'Teaching Tools')
      : userRoles.includes('parent') || userRoles.includes('student')
        ? t('sidebar.schoolLife', 'School life')
        : t('sidebar.management', 'Management')

  const renderNavigationGroup = (items: NavigationItem[], title?: string) => (
    <div className="space-y-0.5">
      {title && (
        <div className="px-3 pt-5 pb-1">
          <p className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
            {title}
          </p>
        </div>
      )}
      {items.map((item) => {
        const Icon = item.icon
        const currentPath = `${location.pathname}${location.search}`
        const isActive = item.href.includes('?')
          ? currentPath === item.href
          : location.pathname === item.href || location.pathname.startsWith(item.href + '/')

        return (
          <Link
            key={item.name}
            to={item.href}
            onClick={onItemClick}
            className={cn(
              'group relative flex items-center rounded-[10px] px-3 py-2.5 text-sm font-medium transition-all duration-200',
              isActive
                ? 'bg-[#EAF1FC] dark:bg-primary-900/40 text-[#073584] dark:text-primary-300 before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:h-5 before:w-1 before:rounded-r-full before:bg-[#0B4DBA]'
                : 'text-slate-600 dark:text-gray-400 hover:bg-[#F1F5F9] dark:hover:bg-gray-800/40 hover:text-slate-900 dark:hover:text-gray-200',
              className
            )}
            title={item.description ? t(item.description) : undefined}
          >
            <Icon
              className={cn(
                'mr-3 h-[18px] w-[18px] flex-shrink-0 transition-colors duration-200',
                isActive ? 'text-[#0B4DBA] dark:text-primary-400' : 'text-slate-400 dark:text-gray-500 group-hover:text-slate-500 dark:group-hover:text-gray-400'
              )}
            />
            <span className="flex-1">
              {t(`common.${item.name}`, item.name.replace(/-/g, ' '))}
            </span>

            {/* Badges */}
            <div className="flex items-center gap-1">
              {item.badge && (
                <Badge
                  variant="secondary"
                  className="text-xs bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300"
                >
                  {t(`nav.badge.${item.badge}`, item.badge)}
                </Badge>
              )}
              {item.isPremium && (
                <Crown className="h-3 w-3 text-yellow-500" />
              )}
            </div>
          </Link>
        )
      })}
    </div>
  )

  return (
    <nav className="flex-1 space-y-1 px-2 py-3 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
      {groupedNavigation.pinned.length > 0 && (
        <>
          {renderNavigationGroup(groupedNavigation.pinned, t('sidebar.pinned', 'Pinned'))}
        </>
      )}

      {groupedNavigation.recents.length > 0 && (
        <>
          <div className="mx-3 border-t border-gray-100 dark:border-gray-800" />
          {renderNavigationGroup(groupedNavigation.recents, t('sidebar.recents', 'Recents'))}
        </>
      )}

      {/* Main Navigation */}
      {groupedNavigation.main.length > 0 && (
        <>
          {(groupedNavigation.pinned.length > 0 || groupedNavigation.recents.length > 0) && (
            <div className="mx-3 border-t border-gray-100 dark:border-gray-800" />
          )}
          {renderNavigationGroup(groupedNavigation.main, isAdminUser ? t('sidebar.main', 'Main') : undefined)}
        </>
      )}

      {/* Management (Teachers/Admins) */}
      {groupedNavigation.management.length > 0 && (
        <>
          <div className="mx-3 border-t border-gray-100 dark:border-gray-800" />
          {renderNavigationGroup(groupedNavigation.management, managementTitle)}
        </>
      )}

      {/* Communication */}
      {groupedNavigation.communication.length > 0 && (
        <>
          <div className="mx-3 border-t border-gray-100 dark:border-gray-800" />
          {renderNavigationGroup(groupedNavigation.communication, isAdminUser ? t('sidebar.commsSetup', 'Comms & setup') : t('sidebar.communication', 'Communication'))}
        </>
      )}
    </nav>
  )
}

// Hook to get navigation items for a specific role
export function useRoleBasedNavigation(role?: string) {
  const { user } = useAuth()
  const userRoles = role ? [role] : (Array.isArray(user?.roles) ? user.roles : [user?.primary_role || 'student'])

  return navigationItems.filter(item =>
    item.roles.some(itemRole => userRoles.includes(itemRole))
  )
}

// Utility to check if user has access to a specific route.
// Compares pathnames only, so query-param hrefs (e.g. `/app/school-management?tab=fees`)
// still grant access to their base path (`/app/school-management`).
export function hasRouteAccess(route: string, userRoles: string[]): boolean {
  const basePath = (href: string) => href.split('?')[0]
  const routePath = basePath(route)
  const matches = navigationItems.filter(nav => basePath(nav.href) === routePath)
  if (matches.length === 0) return false

  return matches.some(item => item.roles.some(role => userRoles.includes(role)))
}

// Get navigation item by route
export function getNavigationItem(route: string): NavigationItem | undefined {
  return navigationItems.find(item =>
    item.href === route || route.startsWith(item.href + '/')
  )
}
